// Executive tab (SG-Exec-Reports only). Three download panels + a full copy of
// the Management P&L rendered in-page (no inner scroller — the page scrolls).
//
//   1. Retail & Tea Room Analysis — season selector + Online/Whitby pills +
//      "Download workbook" (the By-Hour model as a live-formula .xlsx).
//   2. Season Actuals — season selector + "Download workbook": one season's
//      daily services AS RUN, day by day — what IS, not what could be (Chris,
//      2026-09-03). Sales from the tills, services from the working timetable,
//      costs from the Studio's accounts model on that season's own P&L.
//   3. P&L — season selector + "Download P&L" (.xlsx), and below it the whole
//      Management P&L, same layout as the Budget tab.
//
// All data comes from exec-gated endpoints; the button is hidden for non-exec
// users and every endpoint re-checks requireExec server-side.
import { useState, useEffect, useCallback, Fragment } from 'react'
import { downloadRetailTearoomXlsx, type RtPayload } from './execRetailTearoomXlsx'
import { downloadPnlXlsx, type PnlXlsxData } from './execPnlXlsx'
import { buildSeasonActuals, type SaPayload, type SaSeasonRow } from './execSeasonActuals'
import { downloadSeasonActualsXlsx } from './execSeasonActualsXlsx'
import { loadBaseline } from './studio/baseline'
import type { CostConfig, StudioCostsResponse } from './studio/costLines'
import './executive.css'

// ---- P&L types (mirror the Budget-tab render) -----------------------------
interface PnlLine { values: number[]; ytd: number }
interface PnlSub extends PnlLine { sub: string }
interface PnlCategory { category: string; total: PnlLine; subs: PnlSub[] }
interface PnlMonth { period_no: number; name: string; short: string; cal_year: number; locked: boolean }
interface PnlData {
  season: string
  entity: string
  entity_label: string
  months: PnlMonth[]
  current_period: number | null
  last_refresh: string | null
  has_data: boolean
  categories: PnlCategory[]
  operating: PnlLine
  exceptional: { subs: PnlSub[]; total: PnlLine }
  total: PnlLine
}

const gbp0 = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })
const num0 = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })
function pnlText(n: number, showPound: boolean, signedLine: boolean): string {
  const v = Math.round(n)
  if (v === 0) return '–'
  const body = showPound ? gbp0.format(Math.abs(v)) : num0.format(Math.abs(v))
  return signedLine && v < 0 ? `-${body}` : body
}

interface SeasonOpt { fy: number; season: string; label: string }
/** Build "this / last season" options from the endpoint's fiscal-year echo. */
function seasonOpts(cur: number, curLabel: string, prev: number, prevLabel: string): SeasonOpt[] {
  return [
    { fy: cur, season: `${cur - 1}/${cur}`, label: `${curLabel} — this season` },
    { fy: prev, season: `${prev - 1}/${prev}`, label: `${prevLabel} — last season` },
  ]
}

export default function ExecutiveScreen() {
  // Season metadata (fiscal years + labels) — learned from the retail endpoint's
  // default response so we don't hard-code the current year on the client.
  const [opts, setOpts] = useState<SeasonOpt[] | null>(null)
  const [metaErr, setMetaErr] = useState<string | null>(null)

  // Retail & Tea Room Analysis panel state
  const [rtFy, setRtFy] = useState<number | null>(null)
  const [allSeasons, setAllSeasons] = useState<{ fy: number; label: string }[]>([])
  const [comparisonFy, setComparisonFy] = useState<number | null>(null)
  const [cogsSpreadsheet, setCogsSpreadsheet] = useState(false)
  const [includeOnline, setIncludeOnline] = useState(false)
  const [includeWhitby, setIncludeWhitby] = useState(false)
  const [rtBusy, setRtBusy] = useState(false)
  const [rtErr, setRtErr] = useState<string | null>(null)

  // P&L panel + render state
  const [pnlSeason, setPnlSeason] = useState<string | null>(null)
  const [pnlBusy, setPnlBusy] = useState(false)
  const [pnlDlErr, setPnlDlErr] = useState<string | null>(null)

  // Season Actuals panel state (the seasons come from dbo.Seasons via the feed)
  const [saSeasons, setSaSeasons] = useState<SaSeasonRow[]>([])
  const [saSeason, setSaSeason] = useState<string | null>(null)
  const [saBusy, setSaBusy] = useState(false)
  const [saProgress, setSaProgress] = useState<string | null>(null)
  const [saErr, setSaErr] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    fetch('/api/dashboards/exec/season-actuals')
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<{ seasons: SaSeasonRow[] }> })
      .then((d) => {
        if (cancelled) return
        const rows = d.seasons ?? []
        setSaSeasons(rows)
        // Default to the most recent season that has finished trading, if any;
        // else the current one. Nothing is chosen silently — it shows in the box.
        const today = new Date().toISOString().slice(0, 10)
        const completed = rows.find((r) => r.end_date < today)
        setSaSeason((completed ?? rows[0])?.year_label ?? null)
      })
      .catch((e) => { if (!cancelled) setSaErr(e instanceof Error ? e.message : String(e)) })
    return () => { cancelled = true }
  }, [])
  const downloadSeasonActuals = useCallback(async () => {
    if (!saSeason) return
    setSaBusy(true); setSaErr(null); setSaProgress('Reading the season’s sales…')
    try {
      const r = await fetch(`/api/dashboards/exec/season-actuals?season=${encodeURIComponent(saSeason)}`)
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) }
      const payload = (await r.json()) as SaPayload
      const sa = await buildSeasonActuals(payload, {
        baselineFor: async (iso) => { const w = await loadBaseline(undefined, iso); return w.available ? w : null },
        costsFor: async (season) => {
          const c = await fetch(`/api/research/studio-costs?basis=${encodeURIComponent(season)}`)
          if (!c.ok) return null
          return (await c.json()) as StudioCostsResponse
        },
        costConfig: async () => {
          const d = await fetch('/api/research/studio-defaults')
          if (!d.ok) return {}
          const j = (await d.json()) as { defaults?: { costConfig?: CostConfig } }
          return j.defaults?.costConfig ?? {}
        },
        onProgress: (t) => setSaProgress(t),
      })
      setSaProgress('Writing the workbook…')
      await downloadSeasonActualsXlsx(sa)
    } catch (e) {
      setSaErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSaBusy(false); setSaProgress(null)
    }
  }, [saSeason])

  useEffect(() => {
    let cancelled = false
    fetch('/api/dashboards/exec/retail-tearoom-analysis')
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<RtPayload> })
      .then((d) => {
        if (cancelled) return
        const o = seasonOpts(d.fiscalYear, d.fiscalYearLabel, d.prevFiscalYear, d.prevFiscalYearLabel)
        setOpts(o); setRtFy(o[0].fy); setPnlSeason(o[0].season)
        setAllSeasons((d.years ?? []).map((y) => ({ fy: y, label: d.seasons?.[String(y)]?.fiscalYearLabel ?? String(y) })))
        setComparisonFy(d.prevFiscalYear ?? null)
      })
      .catch((e) => { if (!cancelled) setMetaErr(e instanceof Error ? e.message : String(e)) })
    return () => { cancelled = true }
  }, [])

  const downloadRetail = useCallback(async () => {
    if (rtFy == null) return
    setRtBusy(true); setRtErr(null)
    try {
      const r = await fetch(`/api/dashboards/exec/retail-tearoom-analysis?fiscalYear=${rtFy}`)
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) }
      const payload = (await r.json()) as RtPayload
      await downloadRetailTearoomXlsx(payload, {
        includeOnline,
        includeWhitby,
        comparisonFy: comparisonFy === rtFy ? null : comparisonFy,
        cogsMode: cogsSpreadsheet ? 'spreadsheet' : 'actual',
      })
    } catch (e) {
      setRtErr(e instanceof Error ? e.message : String(e))
    } finally {
      setRtBusy(false)
    }
  }, [rtFy, includeOnline, includeWhitby, comparisonFy, cogsSpreadsheet])

  const downloadPnl = useCallback(async () => {
    if (!pnlSeason) return
    setPnlBusy(true); setPnlDlErr(null)
    try {
      const r = await fetch(`/api/dashboards/exec/pnl?season=${encodeURIComponent(pnlSeason)}`)
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) }
      const data = (await r.json()) as PnlXlsxData & { has_data?: boolean }
      await downloadPnlXlsx(data)
    } catch (e) {
      setPnlDlErr(e instanceof Error ? e.message : String(e))
    } finally {
      setPnlBusy(false)
    }
  }, [pnlSeason])

  return (
    <div className="exec-screen">
      <header className="exec-head">
        <p>Exec-only downloads and the management P&amp;L. Visible to SG-Exec-Reports only.</p>
      </header>

      {metaErr && <div className="sales-empty sales-empty--error">{metaErr}</div>}

      {/* ---- Panel 1: Retail & Tea Room Analysis ---- */}
      <section className="exec-panel">
        <div className="exec-panel__head">
          <h2>Retail &amp; Tea Room Analysis</h2>
          <p>The by-hour retail &amp; tea-room model as a spreadsheet: hourly takings per outlet with the per-hour breakdown, cost of goods and labour split by sales, the management-accounts reconciliation, the with-forecast sheets and an optional comparison season. Live formulas.</p>
        </div>
        <div className="exec-panel__controls">
          <label className="exec-field">
            <span>Season</span>
            <select value={rtFy ?? ''} onChange={(e) => setRtFy(Number(e.target.value))} disabled={!opts}>
              {opts?.map((o) => <option key={o.fy} value={o.fy}>{o.label}</option>)}
            </select>
          </label>
          <label className="exec-field">
            <span>Compare with</span>
            <select value={comparisonFy ?? ''} onChange={(e) => setComparisonFy(e.target.value === '' ? null : Number(e.target.value))} disabled={!allSeasons.length}>
              <option value="">No comparison</option>
              {allSeasons.filter((s) => s.fy !== rtFy).map((s) => <option key={s.fy} value={s.fy}>{s.label}</option>)}
            </select>
          </label>
          <button type="button" className={`exec-pill${cogsSpreadsheet ? ' active' : ''}`} aria-pressed={cogsSpreadsheet} onClick={() => setCogsSpreadsheet((v) => !v)} title="ON: the fixed per-outlet COGS % from the source spreadsheet. OFF: the season's actual COGS %.">Spreadsheet COGS %</button>
          <button type="button" className={`exec-pill${includeOnline ? ' active' : ''}`} aria-pressed={includeOnline} onClick={() => setIncludeOnline((v) => !v)} title="Add an Online (Shopify + mail order) retail outlet block">Online Sales Included</button>
          <button type="button" className={`exec-pill${includeWhitby ? ' active' : ''}`} aria-pressed={includeWhitby} onClick={() => setIncludeWhitby((v) => !v)} title="Add Whitby retail as an outlet">Whitby Retail On</button>
          <button type="button" className="btn-primary exec-download" onClick={downloadRetail} disabled={rtBusy || rtFy == null}>
            {rtBusy ? 'Building…' : 'Download workbook'}
          </button>
        </div>
        {rtErr && <div className="sales-empty sales-empty--error">{rtErr}</div>}
      </section>

      {/* ---- Panel 2: Season Actuals — what IS, not what could be ---- */}
      <section className="exec-panel">
        <div className="exec-panel__head">
          <h2>Season Actuals</h2>
          <p>One season&apos;s daily services <strong>as run</strong>, running day by running day — not a model of a future season. A Summary by month (services ran, max locomotives, passengers, tickets sold, revenue, costs, profit) with every ticket type sold; a sheet per month with each running day (profit green, loss red) and how many of its services paid their way; then every profit-making day with its profitable services, and every loss-making day with its loss-making services. Sales from the tills, services from the working timetable, costs from the Timetable Studio&apos;s accounts model on that season&apos;s own P&amp;L.</p>
        </div>
        <div className="exec-panel__controls">
          <label className="exec-field">
            <span>Season</span>
            <select value={saSeason ?? ''} onChange={(e) => setSaSeason(e.target.value)} disabled={saSeasons.length === 0}>
              {saSeasons.map((s) => <option key={s.year_label} value={s.year_label}>{s.year_label} ({s.start_date.slice(0, 7)} to {s.end_date.slice(0, 7)})</option>)}
            </select>
          </label>
          <button type="button" className="btn-primary exec-download" onClick={downloadSeasonActuals} disabled={saBusy || !saSeason}>
            {saBusy ? (saProgress ?? 'Building…') : 'Download workbook'}
          </button>
        </div>
        {saErr && <div className="sales-empty sales-empty--error">{saErr}</div>}
      </section>

      {/* ---- Panel 3: P&L download ---- */}
      <section className="exec-panel">
        <div className="exec-panel__head">
          <h2>P&amp;L</h2>
          <p>The management P&amp;L (from iplicit) as a spreadsheet — a column per month plus year-to-date, with live subtotals.</p>
        </div>
        <div className="exec-panel__controls">
          <label className="exec-field">
            <span>Season</span>
            <select value={pnlSeason ?? ''} onChange={(e) => setPnlSeason(e.target.value)} disabled={!opts}>
              {opts?.map((o) => <option key={o.season} value={o.season}>{o.label}</option>)}
            </select>
          </label>
          <button type="button" className="btn-primary exec-download" onClick={downloadPnl} disabled={pnlBusy || !pnlSeason}>
            {pnlBusy ? 'Building…' : 'Download P&L'}
          </button>
        </div>
        {pnlDlErr && <div className="sales-empty sales-empty--error">{pnlDlErr}</div>}
      </section>

      {/* ---- In-tab Management P&L (no inner scroller) ---- */}
      <ExecPnlTable season={pnlSeason} />
    </div>
  )
}

// One P&L line across the year (label + a value per month + YTD).
function PnlValueRow({ label, line, kind, indent, showPound }: {
  label: string; line: PnlLine
  kind?: 'sub' | 'subtotal' | 'result' | 'total'; indent?: boolean; showPound: boolean
}) {
  const cls = `pnl-row${kind ? ` pnl-row--${kind}` : ''}`
  const signedLine = kind === 'result' || kind === 'total'
  const cells = [...line.values, line.ytd]
  return (
    <tr className={cls}>
      <td className={`pnl-label${indent ? ' pnl-indent' : ''}`}>{label}</td>
      {cells.map((v, i) => (
        <td key={i} className={`ta-right pnl-num${i === cells.length - 1 ? ' pnl-ytd' : ''}`}>
          {pnlText(v, showPound, signedLine)}
        </td>
      ))}
    </tr>
  )
}

// In-tab Management P&L — same layout as the Budget tab, exec-gated endpoint,
// rendered full-height (no fixed-height scroll box; the page scrolls).
function ExecPnlTable({ season }: { season: string | null }) {
  const [data, setData] = useState<PnlData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPound, setShowPound] = useState(false)

  useEffect(() => {
    if (!season) return
    let cancelled = false
    setLoading(true); setError(null)
    fetch(`/api/dashboards/exec/pnl?season=${encodeURIComponent(season)}`)
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<PnlData> })
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [season])

  const monthCols = data?.months ?? []

  return (
    <section className="budget-pnl exec-pnl">
      <div className="sales-section-head">
        <h2>Management P&amp;L — {data?.entity_label ?? 'PLC'} <span style={{ fontWeight: 400, color: '#6b6b6b', fontSize: '0.85rem' }}>(from iplicit)</span></h2>
        <span className="sales-section-sub">
          Income shown positive; expenses listed as costs. Each month is a column, plus year-to-date.
          {data?.last_refresh && ` · updated ${new Date(data.last_refresh).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })}`}
        </span>
      </div>

      <div className="exec-pnl-wrap">
        {loading ? (
          <div className="sales-empty">Loading…</div>
        ) : error ? (
          <div className="sales-empty sales-empty--error">{error}</div>
        ) : !data || !data.has_data ? (
          <div className="sales-empty">No P&amp;L loaded for {season} yet — the nightly iplicit pull may not have run.</div>
        ) : (
          <>
            <div className="pnl-controls">
              <span className="pnl-controls__month">Season {data.season}</span>
              <div className="pnl-controls__right">
                <button type="button" className={`pnl-pound-pill${showPound ? ' active' : ''}`} aria-pressed={showPound} onClick={() => setShowPound((v) => !v)} title="Show or hide the £ symbol on amounts">£</button>
              </div>
            </div>
            <table className="svc-table pnl-table pnl-table--wide">
              <thead>
                <tr>
                  <th className="pnl-label">&nbsp;</th>
                  {monthCols.map((m) => {
                    const isCur = m.period_no === data.current_period
                    return (
                      <th key={m.period_no} className={`ta-right pnl-mth${m.locked ? ' pnl-mth--locked' : ''}${isCur ? ' pnl-mth--current' : ''}`}>
                        <div className="pnl-mth__inner">
                          <span className="pnl-mth__label">{m.short}&nbsp;<span className="pnl-mth__yr">’{String(m.cal_year).slice(2)}</span></span>
                          <span className={`pnl-mth__status pnl-mth__status--${m.locked ? 'final' : 'draft'}`}>{m.locked ? 'FINAL' : 'DRAFT'}</span>
                        </div>
                      </th>
                    )
                  })}
                  <th className="ta-right pnl-ytd">Year to date</th>
                </tr>
              </thead>
              <tbody>
                {data.categories.map((c) => (
                  <Fragment key={c.category}>
                    <PnlValueRow label={c.category} line={c.total} kind="subtotal" showPound={showPound} />
                    {c.subs.map((s) => <PnlValueRow key={s.sub} label={s.sub} line={s} kind="sub" indent showPound={showPound} />)}
                  </Fragment>
                ))}
                <PnlValueRow label="Operating result (Profit & Loss)" line={data.operating} kind="result" showPound={showPound} />
                {data.exceptional.subs.length > 0 && (
                  <>
                    <PnlValueRow label="Exceptional Items" line={data.exceptional.total} kind="subtotal" showPound={showPound} />
                    {data.exceptional.subs.map((s) => <PnlValueRow key={s.sub} label={s.sub} line={s} kind="sub" indent showPound={showPound} />)}
                  </>
                )}
                <PnlValueRow label="Total" line={data.total} kind="total" showPound={showPound} />
              </tbody>
            </table>
          </>
        )}
      </div>
    </section>
  )
}
